import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { ArrowRight, RefreshCw, Equal } from 'lucide-react';

/**
 * LinkKindPopover — what did you just draw?
 *
 * A dropped link is one of three things, and they are different kinds of
 * claim. A connector says this also leads to that: another parent for a node
 * with more than one cause. A loop says the chain comes back on itself. And
 * "on sama" says there is no relation here at all — the two cards are one
 * thing written twice, which is what a map built in several branches at once
 * produces constantly.
 *
 * That last one sat in the effect-type list for a while, among "vahvistaa" and
 * "heikentää", which put an identity claim among the causal ones and made it
 * look like a weak kind of influence. It belongs here, beside the other two
 * things a dropped link can mean.
 *
 * Asked in place at the drop point (specs/impact-path-v2-prd.md §2.6). The
 * first two continue to the type list, because either way the link needs a
 * polarity — and for a loop that polarity decides whether it reinforces or
 * balances. "On sama" needs nothing further: it is already the whole claim.
 */

const appear = keyframes`
  from { opacity: 0; transform: translate(-8px, -50%) scale(0.96); }
  to   { opacity: 1; transform: translate(0, -50%) scale(1); }
`;

const Catcher = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: transparent;
`;

const Card = styled.div`
  position: fixed;
  z-index: 1201;
  transform: translateY(-50%);
  animation: ${appear} 0.14s cubic-bezier(0.2, 0.9, 0.3, 1);
  padding: 6px;
  border-radius: 14px;
  background: #14141f;
  border: 1px solid rgba(255, 255, 255, 0.13);
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  gap: 1px;
  width: 300px;
`;

const Choice = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 11px;
  width: 100%;
  padding: 10px 13px;
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
  background: transparent;
  border: 1px solid transparent;
  text-align: left;
  font-family: inherit;
  transition: background 0.12s ease, border-color 0.12s ease, transform 0.12s ease;

  .icon { flex-shrink: 0; width: 16px; height: 16px; margin-top: 2px; color: ${(p) => p.$color}; }
  /* Spans are inline by default, which ran the label and its explanation into
     one line and stretched the card across half the map. */
  .text { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .label { font-size: 0.94rem; font-weight: 600; }
  .sub { font-size: 0.76rem; line-height: 1.35; color: rgba(255, 255, 255, 0.45); }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: ${(p) => p.$color};
    transform: translateX(2px);
  }
`;

export default function LinkKindPopover({ anchor, onSelect, onClose }) {
  const cardRef = useRef(null);
  const [placement, setPlacement] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useLayoutEffect(() => {
    if (!anchor || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const margin = 12;
    const gap = 24;
    const half = rect.height / 2;
    const fitsRight = anchor.x + gap + rect.width <= window.innerWidth - margin;
    const left = fitsRight ? anchor.x + gap : Math.max(margin, anchor.x - gap - rect.width);
    const top = Math.min(Math.max(anchor.y, half + margin), window.innerHeight - half - margin);
    setPlacement({ left, top });
  }, [anchor]);

  if (!anchor) return null;

  return (
    <>
      <Catcher onClick={onClose} />
      <Card ref={cardRef} style={{ left: placement.left, top: placement.top }} onClick={(e) => e.stopPropagation()}>
        <Choice type="button" $color="#42A5F5" onClick={() => onSelect?.('connection')}>
          <ArrowRight className="icon" />
          <span className="text">
            <span className="label">Yhteys</span>
            <span className="sub">Tämäkin johtaa tuohon — solmulla on useampi syy.</span>
          </span>
        </Choice>
        <Choice type="button" $color="#D4A72C" onClick={() => onSelect?.('loop')}>
          <RefreshCw className="icon" />
          <span className="text">
            <span className="label">Silmukka</span>
            <span className="sub">Ketju palaa takaisin itseensä — vahvistava tai tasapainottava kierre.</span>
          </span>
        </Choice>
        <Choice type="button" $color="#8E9BB3" onClick={() => onSelect?.('same')}>
          <Equal className="icon" />
          <span className="text">
            <span className="label">On sama</span>
            <span className="sub">Sama asia sanottu kahdesti eri haaroissa — ei vaikutusta vaan päällekkäisyys.</span>
          </span>
        </Choice>
      </Card>
    </>
  );
}
